"use client";
import dynamic from "next/dynamic";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { NextIntlClientProvider, useLocale, type AbstractIntlMessages } from "next-intl";
import { apiFetch } from "@/lib/api-client";
import type { memberMcpOptions, memberMcpConfig } from "@/services/ai-mcp-member-service";
import { Dialog, DialogContent, DialogTitleBar } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ContentSkeleton } from "@/components/site/content-skeleton";
import palette from "@/styles/site-palette.module.css";

type Options = Awaited<ReturnType<typeof memberMcpOptions>> & { clientMessages?: AbstractIntlMessages };
type Config = Awaited<ReturnType<typeof memberMcpConfig>>;
const EditorForm = dynamic(() => import("./mcp-connector-editor-form"), { ssr: false, loading: () => <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5"><ContentSkeleton variant="form" count={4} /></div> });

export function McpConnectorEditor({ id, viewerKey, onClose, onSaved }: { id?: string; viewerKey: string; onClose: () => void; onSaved: () => Promise<void> }) {
  const locale = useLocale();
  const [busy, setBusy] = useState(false);
  const query = useQuery({ queryKey: ["mcp-editor", viewerKey, id ?? "new"], gcTime: 0, refetchOnWindowFocus: false, retry: false,
    queryFn: async ({ signal }) => {
      const [options, config] = await Promise.all([
        apiFetch<Options>("/api/member/ai/mcp/manage", { signal }),
        id ? apiFetch<Config>(`/api/member/ai/mcp/manage/${id}`, { signal }) : Promise.resolve(null),
      ]);
      return { options, config };
    } });

  return <Dialog open onOpenChange={open => { if (!open && !busy) onClose(); }}><DialogContent className={`${palette.palette} flex h-[min(720px,calc(100svh-30px))] max-w-[min(840px,calc(100%-30px))] flex-col gap-0 overflow-hidden p-0`}>
    <DialogTitleBar className="px-6" title={id ? "编辑连接器" : "添加连接器"} />
    {query.data ? <NextIntlClientProvider locale={locale} messages={query.data.options.clientMessages}><EditorForm id={id} options={query.data.options} config={query.data.config} busy={busy} setBusy={setBusy} onClose={onClose} onSaved={onSaved} /></NextIntlClientProvider> : <div className="min-h-0 flex-1 overflow-hidden px-6 py-5">{query.isError ? <p role="alert">加载失败<Button variant="link" onClick={() => void query.refetch()}>重试</Button></p> : <ContentSkeleton variant="form" count={6} />}</div>}
  </DialogContent></Dialog>;
}
